import { posthog } from "posthog-js";

export interface TrackingProperties {
    [key: string]: any;
}

export interface UserProperties {
    email?: string;
    name?: string;
    plan?: string;
    team_id?: string;
    [key: string]: any;
}

export const usePostHog = () => {
    const initPostHog = (apiKey: string, options: any = {}) => {
        if (typeof window === "undefined") {
            return;
        }

        const urlParams = new URLSearchParams(window.location.search);
        const bootstrapDistinctId =
            urlParams.get("ph_distinct_id") || undefined;
        const bootstrapSessionId = urlParams.get("ph_session_id") || undefined;

        posthog.init(apiKey, {
            api_host: "https://t.zaptime.app",
            defaults: "2026-01-30",
            capture_pageview: false, // We'll handle this manually
            cross_subdomain_cookie: true,
            secure_cookie: true,
            persistence: "localStorage+cookie",
            bootstrap: {
                distinctID: bootstrapDistinctId,
                sessionID: bootstrapSessionId,
            },
            ...options,
        });

        // For returning visitors, bootstrap is ignored because PostHog already
        // has a stored identity. Alias the landing-page id so events from
        // zaptime.cz merge into the same person profile.
        if (
            bootstrapDistinctId &&
            posthog.__loaded &&
            posthog.get_distinct_id() !== bootstrapDistinctId
        ) {
            posthog.alias(bootstrapDistinctId);
        }

        // Strip cross-domain params so they don't leak via referrer/bookmarks.
        if (bootstrapDistinctId || bootstrapSessionId) {
            const url = new URL(window.location.href);
            url.searchParams.delete("ph_distinct_id");
            url.searchParams.delete("ph_session_id");
            window.history.replaceState({}, "", url.toString());
        }
    };

    const identifyUser = (userId: string, properties?: UserProperties) => {
        if (!posthog.__loaded) {
            return;
        }
        posthog.identify(userId, properties);
    };

    const trackEvent = (eventName: string, properties?: TrackingProperties) => {
        if (!posthog.__loaded) {
            return;
        }
        posthog.capture(eventName, properties);
    };

    const trackPageView = (url?: string) => {
        if (!posthog.__loaded) {
            return;
        }
        posthog.capture("$pageview", {
            $current_url: url || window.location.href,
        });
    };

    const trackSignup = (properties?: TrackingProperties) => {
        trackEvent("user_signup", {
            ...properties,
            $set: {
                signup_date: new Date().toISOString(),
            },
        });
    };

    const trackSubscription = (
        plan: string,
        amount: number,
        currency: string = "CZK",
        properties?: TrackingProperties,
    ) => {
        trackEvent("subscription_created", {
            plan,
            amount,
            currency,
            ...properties,
            $set: {
                subscription_plan: plan,
                subscription_date: new Date().toISOString(),
            },
        });
    };

    return {
        initPostHog,
        identifyUser,
        trackEvent,
        trackPageView,
        trackSignup,
        trackSubscription,
    };
};
